
    import debounce from 'lodash/debounce';
    import has from 'lodash/has';
    import { mapActions } from 'vuex';
    import { ValidationProvider } from 'vee-validate';
    import C1OccupationSelector from '@/C1Components/C1OccupationSelector.vue';
    import C1TypeaheadSingleSelect from '@/C1Components/C1TypeaheadSingleSelect.vue';
    import C1Label from '@/C1Components/C1Label';
    import C1HelpText from '@/C1Components/C1HelpText';
    import WhatIsThis from '@/components/Utils/WhatIsThis';
    import C1Spinner from '@/C1Components/C1Spinner';

    export default {
        name: 'SeekerRecentRoleCombo',

        components: {
            C1Spinner,
            WhatIsThis,
            C1HelpText,
            C1Label,
            ValidationProvider,
            C1OccupationSelector,
            C1TypeaheadSingleSelect,
        },

        props: {
            jobTitleLabel: {
                type: String,
                default: 'Job title',
            },

            occupationLabel: {
                type: String,
                default: 'Occupation',
            },

            isRequired: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                workExperience: {},
                jobTitle: {},
                jobTitleOptions: [],
                occupation: {
                    search: '',
                    occupations: [],
                },
                occupationOptions: [],
                spinner: {
                    get_init_data: false,
                },
            };
        },

        computed: {
            jobTitleValidationRules() {
                return this.isRequired ? 'required|object:label' : '';
            },

            occupationValidationRules() {
                return this.isRequired ? 'occupation' : '';
            },
        },

        watch: {
            jobTitle: {
                handler(value) {
                    if (value.job_title && !this.occupation.occupations.length) {
                        this.searchOccupationByJobTitle(value.job_title);
                    }
                },
                deep: true,
            },
        },

        created() {
            this.getInitData();
        },

        methods: {
            ...mapActions('me', {
                $_getWorkExperiences: 'getWorkExperiences',
            }),

            ...mapActions('occupation', {
                $_getOccupationSuggestions: 'getOccupationSuggestions',
                $_searchOccupations: 'searchOccupations',
            }),

            ...mapActions('job', {
                $_getJobTitleSuggestion: 'getJobTitleSuggestion',
            }),

            ...mapActions('category', {
                $_getCategoriesWithOccupation: 'getCategoriesWithOccupation',
            }),

            getInitData() {
                this.spinner.get_init_data = true;
                this.$emit('initializing', true);
                Promise.all([this.$_getWorkExperiences(), this.$_getCategoriesWithOccupation()]).then((response) => {
                    if (response[0].data.length) {
                        this.workExperience = response[0].data[0];
                        this.jobTitle = { label: this.workExperience.job_title, id: null };
                        this.occupation = has(this.workExperience, 'occupation.id')
                            ? {
                                search: this.workExperience.occupation.name,
                                occupations: [this.workExperience.occupation],
                            }
                            : { search: '', occupations: [] };
                    }

                    this.categories = response[1];
                }).finally(() => {
                    this.spinner.get_init_data = false;
                    this.$emit('initializing', false);
                });
            },

            jobTitleSearch(query) {
                this.$_getJobTitleSuggestion(query).then((response) => {
                    if (response.data.length) {
                        this.jobTitleOptions = response.data.map((o, idx) => {
                            return {
                                ...o,
                                uniq_id: idx,
                            };
                        });
                    }
                });
            },

            occupationSearch(keyword) {
                if (keyword) {
                    this.$_getOccupationSuggestions({ keyword }).then((response) => {
                        this.occupationOptions = response.data;
                    });
                }
            },

            searchOccupationByJobTitle: debounce(function(query) {
                if (query.length >= 3) {
                    const data = {
                        normalised_job_title: query,
                    };

                    this.$_searchOccupations(data).then((response) => {
                        if (has(response, 'data.id')) {
                            this.occupation.search = response.data.name;
                            this.occupation.occupations = [response.data];
                            this.$nextTick(() => {
                                this.$validator.validate('occupation');
                            });
                        }
                    });
                }
            }, 300),
        },
    };
